import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { Button, Input, Label, NoteMessage, ValidationMessage } from "components/elements";
import { trpc } from "lib/trpc";
import { Disable2FAModal } from "./Disable2FAModal";

export const TwoFactorAuthentication = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [code, setCode] = useState("");
    const [touched, setTouched] = useState(false);
    const [showDisableModal, setShowDisableModal] = useState(false);

    const profileQuery = trpc.profiles.getProfile.useQuery();
    const generate2FA = trpc.profiles.generateTwoFactor.useQuery(undefined, { enabled: false });
    const verifyCode = trpc.profiles.verifyTwoFactor.useMutation();
    const disableTwoFactor = trpc.profiles.disableTwoFactor.useMutation();

    const [qrCodeData, setQrCodeData] = useState<{
        qrCodeDataURL: string;
        otpauthUrl: string;
    } | null>(null);

    const isCodeValid = code.length === 6 && /^\d+$/.test(code);
    const is2FAEnabled = profileQuery.data?.twoFactorEnabled;

    useEffect(() => {
        if (!is2FAEnabled) {
            generate2FA.refetch().then((res) => {
                if (res.data) {
                    setQrCodeData(res.data);
                }
            });
        }
    }, [is2FAEnabled]);

    const handleEnable2FA = async () => {
        try {
            const result = await verifyCode.mutateAsync({ code });

            if (result.success) {
                enqueueSnackbar("Two-Factor Authentication enabled successfully.", {
                    variant: "success",
                });
                await profileQuery.refetch();
            } else {
                enqueueSnackbar("Invalid code. Please try again.", { variant: "error" });
            }
        } catch (e) {
            enqueueSnackbar("Verification failed. Try again.", { variant: "error" });
        }
    };

    const handleDisable2FA = async () => {
        await disableTwoFactor.mutateAsync();
        enqueueSnackbar("Two-Factor Authentication disabled.", { variant: "info" });
        await profileQuery.refetch();
    };

    if (profileQuery.isPending) return null;

    return (
        <div className="bg-brand-polar my-6 flex flex-col rounded-2xl border border-[#c8c8c8] p-6">
            <div className="text-xl font-semibold text-[#424242]">
                Setup Two Factor Authentication
            </div>
            <NoteMessage note="Two factor authentication greatly improves your account security and helps keep your data protected." />

            {is2FAEnabled ? (
                <div className="mt-4">
                    <p className="mb-3 text-sm font-medium text-green-700">
                        Two-Factor Authentication is currently <strong>enabled</strong> on your
                        account.
                    </p>
                    <Button
                        variant="secondary"
                        size="smaller"
                        onClick={() => setShowDisableModal(true)}
                    >
                        Disable Two Factor Authentication
                    </Button>

                    <Disable2FAModal
                        isOpen={showDisableModal}
                        setIsOpen={setShowDisableModal}
                        onDisable={handleDisable2FA}
                    />
                </div>
            ) : (
                <>
                    {qrCodeData && (
                        <>
                            <div className="my-6">
                                <img
                                    src={qrCodeData.qrCodeDataURL}
                                    alt="2FA QR Code"
                                    className="h-40 w-40"
                                />
                            </div>

                            <p className="mb-4 text-sm text-gray-600">
                                Scan the QR code using an authenticator app like Google
                                Authenticator or 1Password, then input the 6-digit code below.
                            </p>

                            <Label htmlFor="2fa-code">Verification Code</Label>
                            <Input
                                id="2fa-code"
                                placeholder="Two Factor Code"
                                value={code}
                                onChange={(e) => {
                                    setCode(e.target.value);
                                    setTouched(true);
                                }}
                            />
                            {!isCodeValid && touched && (
                                <ValidationMessage
                                    error={{
                                        message: "Enter a valid 6-digit code",
                                    }}
                                />
                            )}

                            <div className="mt-4">
                                <Button
                                    variant="primary"
                                    size="smaller"
                                    disabled={!isCodeValid || verifyCode.isPending}
                                    onClick={handleEnable2FA}
                                >
                                    Enable Two Factor Authentication
                                </Button>
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
};
